import { FUNCOES } from './posicoes';
import { POSICOES } from './tipos';

/**
 * Validação de um save antes de o jogo usá-lo (importar arquivo, carregar da conta, ler do navegador, restaurar sala).
 * O save é só dados, mas um arquivo adulterado com tipos errados, referências inexistentes ou listas gigantes derrubaria a tela
 * (e, como o jogo reabre o último save sozinho, derrubaria a tela toda vez). Aqui tudo que a interface assume é conferido:
 * tipos, intervalos, tamanhos e ids que apontam para clubes que existem. Texto nunca é executado: a interface o escapa ao desenhar.
 */

const LIMITE = {
  clubes: 400,
  elenco: 120,
  juniores: 80,
  livres: 5000,
  rodadas: 400,
  confrontosPorRodada: 200,
  resultados: 40_000,
  historico: 2000,
  trofeus: 500,
  texto: 120,
} as const;

/** Maior valor aceito em campos numéricos (dinheiro, contadores): bem acima de qualquer jogo real. */
const NUM_MAX: number = 1e13;

class SaveInvalido extends Error {}

const falha = (onde: string, motivo: string): never => {
  throw new SaveInvalido(`save inválido (${onde}: ${motivo})`);
};

const ehObjeto = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function numero(v: unknown, onde: string, min = -NUM_MAX, max = NUM_MAX): number {
  if (typeof v !== 'number' || !Number.isFinite(v) || v < min || v > max) falha(onde, `número fora do intervalo (${String(v).slice(0, 20)})`);
  return v as number;
}

function inteiro(v: unknown, onde: string, min = -NUM_MAX, max = NUM_MAX): number {
  const n = numero(v, onde, min, max);
  if (!Number.isInteger(n)) falha(onde, 'não é inteiro');
  return n;
}

function texto(v: unknown, onde: string, vazioOk = false): string {
  if (typeof v !== 'string' || v.length > LIMITE.texto || (!vazioOk && v.length === 0)) falha(onde, 'texto inválido ou grande demais');
  return v as string;
}

function lista(v: unknown, onde: string, max: number): unknown[] {
  if (!Array.isArray(v) || v.length > max) falha(onde, `lista inválida ou com mais de ${max} itens`);
  return v as unknown[];
}

function opcional(v: unknown, quando: (x: unknown) => void): void {
  if (v !== undefined && v !== null) quando(v);
}

const ESTILOS = ['retranca', 'defensivo', 'equilibrado', 'ofensivo', 'total'];
const PASSES = ['curto', 'misto', 'longo'];
const PRESSOES = ['baixa', 'media', 'alta'];
const MARCACOES = ['leve', 'normal', 'forte'];
const ATAQUES = ['meio', 'lados', 'equilibrado'];
const FORMACOES = ['auto', '4-4-2', '4-3-3', '4-5-1', '3-5-2', '5-3-2', '3-4-3', '5-4-1', '4-2-3-1', '4-1-4-1', '4-3-1-2', '4-4-1-1', '4-2-4', '4-3-2-1', '3-4-2-1', '3-4-1-2', '3-6-1', '5-2-3'];
const HABILIDADES = ['gol', 'des', 'arm', 'fin', 'vel', 'tec', 'pas'];

function validarJogador(j: unknown, onde: string): void {
  if (!ehObjeto(j)) return falha(onde, 'jogador não é objeto');
  inteiro(j.id, `${onde}.id`, 0);
  texto(j.nome, `${onde}.nome`);
  numero(j.idade, `${onde}.idade`, 10, 70);
  if (!(POSICOES as readonly unknown[]).includes(j.posicao)) falha(onde, 'posição inválida');
  numero(j.overall, `${onde}.overall`, 0, 100);
  numero(j.energia, `${onde}.energia`, 0, 100);
  for (const k of ['salario', 'valor']) numero(j[k], `${onde}.${k}`, 0);
  for (const k of ['lesaoRodadas', 'suspensaoRodadas', 'amarelosAcumulados', 'gols', 'jogos', 'assistencias']) numero(j[k], `${onde}.${k}`, 0, 100_000);
  numero(j.notaSoma, `${onde}.notaSoma`, 0, 1e7);
  if (j.lado !== 'E' && j.lado !== 'D') falha(onde, 'lado inválido');
  const car = lista(j.caracteristicas, `${onde}.caracteristicas`, 2);
  if (car.length !== 2) falha(onde, 'características devem ser 2');
  for (const c of car) inteiro(c, `${onde}.caracteristicas`, 0, 13);
  for (const k of ['nacionalidade', 'nomeCompleto', 'dadosEstimados']) opcional(j[k], (x) => texto(x, `${onde}.${k}`, true));
  for (const k of ['suspLiga', 'suspCopa', 'amarelosLiga', 'amarelosCopa']) opcional(j[k], (x) => numero(x, `${onde}.${k}`, 0, 100_000));
  for (const k of ['numeroCamisa', 'contratoDias', 'talento', 'desenvolvimento', 'progressoTreino']) opcional(j[k], (x) => numero(x, `${onde}.${k}`, -1e6, 1e6));
  opcional(j.funcao, (x) => {
    if (!(FUNCOES as readonly unknown[]).includes(x)) falha(onde, 'função inválida');
  });
  opcional(j.habilidades, (h) => {
    if (!ehObjeto(h)) return falha(onde, 'habilidades inválidas');
    for (const k of HABILIDADES) numero(h[k], `${onde}.habilidades.${k}`, 0, 100);
  });
}

function validarTatica(t: unknown, onde: string, idsJogadores: Set<number>): void {
  if (!ehObjeto(t)) return falha(onde, 'tática inválida');
  if (!FORMACOES.includes(t.formacao as string)) falha(onde, 'formação inválida');
  if (!ESTILOS.includes(t.estilo as string)) falha(onde, 'estilo inválido');
  if (!MARCACOES.includes(t.marcacao as string)) falha(onde, 'marcação inválida');
  if (!ATAQUES.includes(t.ataque as string)) falha(onde, 'ataque inválido');
  opcional(t.passe, (x) => {
    if (!PASSES.includes(x as string)) falha(onde, 'estilo de passe inválido');
  });
  opcional(t.pressao, (x) => {
    if (!PRESSOES.includes(x as string)) falha(onde, 'pressão inválida');
  });
  for (const k of ['titulares', 'reservas'])
    opcional(t[k], (x) => {
      for (const id of lista(x, `${onde}.${k}`, 30)) inteiro(id, `${onde}.${k}`, 0);
    });
  void idsJogadores; // ids de titulares que não existem mais são tratados pela escalação automática do jogo
  for (const k of ['capitaoId', 'batedorPenaltiId', 'batedorFaltaId', 'batedorEscanteioId']) opcional(t[k], (x) => inteiro(x, `${onde}.${k}`, 0));
  opcional(t.substituicoes, (x) => {
    for (const s of lista(x, `${onde}.substituicoes`, 10)) {
      if (!ehObjeto(s)) return falha(onde, 'substituição inválida');
      inteiro(s.saiId, `${onde}.sub`, 0);
      inteiro(s.entraId, `${onde}.sub`, 0);
      numero(s.minuto, `${onde}.sub`, 0, 130);
      if (!['sempre', 'perdendo', 'empatando', 'ganhando'].includes(s.condicao as string)) falha(onde, 'condição inválida');
    }
  });
  opcional(t.ajustes, (x) => {
    for (const a of lista(x, `${onde}.ajustes`, 10)) {
      if (!ehObjeto(a) || !['sempre', 'perdendo', 'empatando', 'ganhando'].includes(a.condicao as string)) falha(onde, 'ajuste inválido');
    }
  });
}

function validarTrofeus(v: unknown, onde: string): void {
  for (const t of lista(v, onde, LIMITE.trofeus)) {
    if (!ehObjeto(t)) return falha(onde, 'troféu inválido');
    inteiro(t.temporada, `${onde}.temporada`, 0, 100_000);
    texto(t.competicao, `${onde}.competicao`);
    if (!['nacional', 'copa', 'estadual', 'internacional'].includes(t.tipo as string)) falha(onde, 'tipo de troféu inválido');
    numero(t.divisao, `${onde}.divisao`, 0, 20);
  }
}

/**
 * Confere o save inteiro e lança Error com o motivo se algo não servir. Itens de resumo que só enfeitam a tela (histórico,
 * transferências recentes, última rodada) que estiverem inválidos são descartados em vez de recusar o save todo.
 */
export function validarSave(dados: unknown): void {
  if (!ehObjeto(dados)) return falha('raiz', 'não é um objeto');
  numero(dados.semente, 'semente');
  numero(dados.rngEstado, 'rngEstado');
  inteiro(dados.temporada, 'temporada', 1, 100_000);
  inteiro(dados.proximoIdJogador, 'proximoIdJogador', 0);

  // os clubes estrangeiros da Libertadores seguem as mesmas regras dos outros e contam como clubes existentes para resultados e calendário
  const externos = ehObjeto(dados.libertadores) ? lista(dados.libertadores.clubesExternos, 'libertadores.clubesExternos', 64) : [];
  const clubes = [...lista(dados.clubes, 'clubes', LIMITE.clubes), ...externos];
  if (clubes.length < 2) falha('clubes', 'poucos clubes');
  const idsClubes = new Set<number>();
  const idsJogadores = new Set<number>();
  clubes.forEach((c, i) => {
    const onde = `clubes[${i}]`;
    if (!ehObjeto(c)) return falha(onde, 'clube não é objeto');
    const id = inteiro(c.id, `${onde}.id`, 0, 1_000_000);
    if (idsClubes.has(id)) falha(onde, 'id de clube repetido');
    idsClubes.add(id);
    texto(c.nome, `${onde}.nome`);
    texto(c.sigla, `${onde}.sigla`);
    texto(c.cor1, `${onde}.cor1`, true);
    texto(c.cor2, `${onde}.cor2`, true);
    opcional(c.divisao, (x) => {
      if (x !== 1 && x !== 2) falha(onde, 'divisão inválida');
    });
    numero(c.nivel, `${onde}.nivel`, 0, 100);
    numero(c.reputacao, `${onde}.reputacao`, 0, 10);
    numero(c.caixa, `${onde}.caixa`);
    numero(c.capacidade, `${onde}.capacidade`, 0, 1_000_000);
    numero(c.confiancaDiretoria, `${onde}.confiancaDiretoria`, 0, 100);
    numero(c.confiancaTorcida, `${onde}.confiancaTorcida`, 0, 100);
    if (typeof c.humano !== 'boolean' || typeof c.usarPrecosSugeridos !== 'boolean') falha(onde, 'campos booleanos inválidos');
    const precos = lista(c.precos, `${onde}.precos`, 4);
    if (precos.length !== 4) falha(onde, 'preços devem ser 4');
    for (const p of precos) numero(p, `${onde}.precos`, 0, 100_000);
    if (!ehObjeto(c.balanco)) falha(onde, 'balanço inválido');
    for (const k of ['bilheteria', 'patrocinio', 'premios', 'salarios', 'transferencias']) numero((c.balanco as Record<string, unknown>)[k], `${onde}.balanco.${k}`);
    opcional(c.tecnico, (x) => texto(x, `${onde}.tecnico`, true));
    opcional(c.emprestimo, (x) => numero(x, `${onde}.emprestimo`, 0));
    const elenco = lista(c.elenco, `${onde}.elenco`, LIMITE.elenco);
    elenco.forEach((j, k) => {
      validarJogador(j, `${onde}.elenco[${k}]`);
      const jid = (j as { id: number }).id;
      if (idsJogadores.has(jid)) falha(onde, `jogador com id repetido (${jid})`);
      idsJogadores.add(jid);
    });
    opcional(c.juniores, (x) => lista(x, `${onde}.juniores`, LIMITE.juniores).forEach((j, k) => validarJogador(j, `${onde}.juniores[${k}]`)));
    opcional(c.trofeus, (x) => validarTrofeus(x, `${onde}.trofeus`));
    validarTatica(c.tatica, `${onde}.tatica`, idsJogadores);
    opcional(c.treino, (t) => {
      if (!ehObjeto(t) || typeof t.foco !== 'string' || typeof t.intensidade !== 'string' || t.foco.length > 20 || t.intensidade.length > 20) falha(onde, 'treino inválido');
    });
  });
  inteiro(dados.humanoId, 'humanoId', 0);
  if (!idsClubes.has(dados.humanoId as number)) falha('humanoId', 'clube do técnico não existe');

  const calendario = lista(dados.calendario, 'calendario', LIMITE.rodadas);
  calendario.forEach((r, i) => {
    for (const c of lista(r, `calendario[${i}]`, LIMITE.confrontosPorRodada)) {
      if (!ehObjeto(c) || !idsClubes.has(c.mandante as number) || !idsClubes.has(c.visitante as number)) falha(`calendario[${i}]`, 'jogo com clube inexistente');
    }
  });
  inteiro(dados.rodada, 'rodada', 0, calendario.length);

  for (const r of lista(dados.resultados, 'resultados', LIMITE.resultados)) {
    if (!ehObjeto(r) || !idsClubes.has(r.mandante as number) || !idsClubes.has(r.visitante as number)) return falha('resultados', 'resultado com clube inexistente');
    inteiro(r.rodada, 'resultados.rodada', 0, calendario.length);
    const g = lista(r.gols, 'resultados.gols', 2);
    if (g.length !== 2) falha('resultados', 'placar inválido');
    for (const x of g) inteiro(x, 'resultados.gols', 0, 99);
  }

  lista(dados.livres, 'livres', LIMITE.livres).forEach((j, k) => validarJogador(j, `livres[${k}]`));
  opcional(dados.tecnico, (x) => texto(x, 'tecnico', true));
  opcional(dados.trofeusTecnico, (x) => validarTrofeus(x, 'trofeusTecnico'));
  if (dados.formato !== undefined && dados.formato !== 'liga' && dados.formato !== 'copa') falha('formato', 'valor inválido');
  if (dados.divisao !== undefined && dados.divisao !== 'a' && dados.divisao !== 'b') falha('divisao', 'valor inválido');
  if (dados.acesso !== undefined && typeof dados.acesso !== 'boolean') falha('acesso', 'valor inválido');

  opcional(dados.copa, (cp) => {
    if (!ehObjeto(cp)) return falha('copa', 'inválida');
    texto(cp.nome, 'copa.nome');
    for (const id of lista(cp.participantes, 'copa.participantes', 64)) if (!idsClubes.has(id as number)) falha('copa', 'participante inexistente');
    for (const k of ['campeaoId', 'viceId']) if (cp[k] !== null && cp[k] !== undefined && !idsClubes.has(cp[k] as number)) falha('copa', `${k} inexistente`);
    opcional(cp.slots, (s) => lista(s, 'copa.slots', 20).forEach((x) => inteiro(x, 'copa.slots', 0, calendario.length)));
    for (const f of lista(cp.fases, 'copa.fases', 8)) {
      if (!ehObjeto(f) || typeof f.concluida !== 'boolean') return falha('copa', 'fase inválida');
      texto(f.nome, 'copa.fase.nome');
      for (const r of lista(f.rodadas, 'copa.fase.rodadas', 3)) inteiro(r, 'copa.fase.rodadas', 0, calendario.length);
      for (const c of lista(f.confrontos, 'copa.fase.confrontos', 32)) {
        if (!ehObjeto(c) || !idsClubes.has(c.a as number) || !idsClubes.has(c.b as number)) return falha('copa', 'confronto com clube inexistente');
        if (c.vencedor !== undefined && !idsClubes.has(c.vencedor as number)) falha('copa', 'vencedor inexistente');
      }
    }
  });

  for (const chave of ['libertadores', 'sulamericana'] as const) {
    opcional(dados[chave], (lb) => {
      if (!ehObjeto(lb)) return falha(chave, 'inválida');
      for (const id of lista(lb.participantes, `${chave}.participantes`, 64)) if (!idsClubes.has(id as number)) falha(chave, 'participante inexistente');
      for (const g of lista(lb.grupos, `${chave}.grupos`, 8)) for (const id of lista(g, `${chave}.grupo`, 4)) if (!idsClubes.has(id as number)) falha(chave, 'clube do grupo inexistente');
      for (const s of lista(lb.slots, `${chave}.slots`, 15)) inteiro(s, `${chave}.slots`, 0, calendario.length);
      for (const k of ['campeaoId', 'viceId']) if (lb[k] !== null && lb[k] !== undefined && !idsClubes.has(lb[k] as number)) falha(chave, ` inexistente`);
      for (const k of ['terceiros', 'lideres']) if (lb[k] !== undefined) for (const id of lista(lb[k], `${chave}.${k}`, 8)) if (!idsClubes.has(id as number)) falha(chave, `${k} inexistente`);
      for (const f of lista(lb.fasesMata, `${chave}.fasesMata`, 5)) {
        if (!ehObjeto(f) || typeof f.concluida !== 'boolean') return falha(chave, 'fase inválida');
        for (const r of lista(f.rodadas, `${chave}.fase.rodadas`, 2)) inteiro(r, `${chave}.fase.rodadas`, 0, calendario.length);
        for (const c of lista(f.confrontos, `${chave}.fase.confrontos`, 8)) {
          if (!ehObjeto(c) || !idsClubes.has(c.a as number) || !idsClubes.has(c.b as number)) return falha(chave, 'confronto com clube inexistente');
        }
      }
    });
  }

  // resumos que só enfeitam a tela: o que estiver quebrado é descartado, não derruba o save
  dados.historico = (Array.isArray(dados.historico) ? dados.historico : [])
    .slice(0, LIMITE.historico)
    .filter(
      (h: unknown) =>
        ehObjeto(h) &&
        Number.isFinite(h.temporada) &&
        Number.isFinite(h.posicaoHumano) &&
        idsClubes.has(h.campeaoId as number) &&
        (h.copaCampeaoId === undefined || idsClubes.has(h.copaCampeaoId as number)) &&
        (h.rebaixados === undefined || (Array.isArray(h.rebaixados) && h.rebaixados.every((id) => idsClubes.has(id)))) &&
        (h.promovidos === undefined || (Array.isArray(h.promovidos) && h.promovidos.every((id) => idsClubes.has(id)))),
    );
  dados.transferencias = (Array.isArray(dados.transferencias) ? dados.transferencias : []).slice(0, 500).filter((t: unknown) => ehObjeto(t) && typeof t.valor === 'number' && Number.isFinite(t.valor));
  const ur = dados.ultimaRodada;
  if (
    ur !== null &&
    ur !== undefined &&
    !(
      ehObjeto(ur) &&
      Array.isArray(ur.partidas) &&
      ur.partidas.length <= LIMITE.confrontosPorRodada &&
      ur.partidas.every((p: unknown) => ehObjeto(p) && idsClubes.has(p.mandante as number) && idsClubes.has(p.visitante as number) && ehObjeto(p.resultado) && Array.isArray(p.resultado.eventos) && Array.isArray(p.resultado.gols) && ehObjeto(p.resultado.estat))
    )
  ) {
    dados.ultimaRodada = null;
  }
}
